import React, { useState, useEffect } from 'react';
import {
    Settings,
    User,
    Lock,
    KeyRound,
    Globe,
    Users,
    Database,
    Shield,
    Save,
    Eye,
    EyeOff,
    Copy,
    Trash2,
    Plus,
    X,
    Loader2,
    Download,
    CheckCircle2,
    AlertTriangle,
    Smartphone,
    Mail,
    Camera,
    MapPin
} from 'lucide-react';
import Swal from 'sweetalert2';
import { API_ENDPOINTS } from '../../config/api';

interface AdminUserItem {
    id: number;
    name: string;
    email: string;
    role: string;
    created_at: string;
}

const SettingsAdmin: React.FC = () => {
    const [activeTab, setActiveTab] = useState<'profile' | 'security' | 'pin' | 'platform' | 'admins' | 'backup'>('profile');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);

    // Current logged-in user data from token
    const [currentUser, setCurrentUser] = useState<{ id?: number; name: string; email: string; role?: string }>(() => {
        try {
            const token = localStorage.getItem('khazanah_token');
            if (token) {
                const payload = JSON.parse(atob(token.split('.')[1]));
                return {
                    id: payload.id,
                    name: payload.name || 'Administrator',
                    email: payload.email || 'admin@khazanah.id',
                    role: payload.role || 'admin'
                };
            }
        } catch (e) {}
        return { name: 'Administrator', email: 'admin@khazanah.id', role: 'admin' };
    });

    // Form Profile
    const [profileName, setProfileName] = useState('');
    const [profileEmail, setProfileEmail] = useState('');

    // Form Password
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showCurrentPass, setShowCurrentPass] = useState(false);
    const [showNewPass, setShowNewPass] = useState(false);

    // Settings (PIN, Registration, Platform)
    const [settingsData, setSettingsData] = useState({
        admin_register_pin: '202688',
        allow_admin_registration: 'true',
        platform_name: 'Khazanah',
        platform_tagline: 'Wadah Kolaborasi Pemuda & Komunitas Islam',
        official_email: 'salam@khazanah.id',
        official_whatsapp: '089663933263',
        instagram_account: '@khazanah_id',
        address: 'Bandung, Jawa Barat, Indonesia'
    });

    // Admin Users List & Modal
    const [adminList, setAdminList] = useState<AdminUserItem[]>([]);
    const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
    const [newAdminForm, setNewAdminForm] = useState({
        name: '',
        email: '',
        password: '',
        role: 'admin'
    });

    // Fetch initial settings & admin list
    const loadSettings = async () => {
        setIsLoading(true);
        try {
            // 1. Fetch system settings
            const setRes = await fetch(API_ENDPOINTS.settings);
            if (setRes.ok) {
                const data = await setRes.json();
                if (data.settings) {
                    setSettingsData(prev => ({
                        ...prev,
                        ...data.settings
                    }));
                }
            }

            // 2. Fetch admin users
            const token = localStorage.getItem('khazanah_token');
            const usersRes = await fetch(`${API_ENDPOINTS.auth}/users`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (usersRes.ok) {
                const users = await usersRes.json();
                setAdminList(users);

                // Sinkronisasi data user aktif
                if (currentUser.id) {
                    const matched = users.find((u: AdminUserItem) => u.id === currentUser.id);
                    if (matched) {
                        setCurrentUser(prev => ({ ...prev, name: matched.name, email: matched.email }));
                        setProfileName(matched.name);
                        setProfileEmail(matched.email);
                    } else {
                        setProfileName(currentUser.name);
                        setProfileEmail(currentUser.email);
                    }
                } else {
                    setProfileName(currentUser.name);
                    setProfileEmail(currentUser.email);
                }
            } else {
                setProfileName(currentUser.name);
                setProfileEmail(currentUser.email);
            }

        } catch (error) {
            console.error('Gagal memuat pengaturan:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadSettings();
    }, []);

    // 1. Simpan Profil Admin
    const handleSaveProfile = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const token = localStorage.getItem('khazanah_token');
            const res = await fetch(`${API_ENDPOINTS.auth}/profile`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    id: currentUser.id,
                    email: currentUser.email,
                    name: profileName,
                    newEmail: profileEmail
                })
            });

            const data = await res.json();
            if (res.ok) {
                setCurrentUser(prev => ({ ...prev, name: profileName, email: profileEmail }));
                Swal.fire({
                    icon: 'success',
                    title: 'Profil Diperbarui',
                    text: 'Data profil admin berhasil disimpan.',
                    confirmButtonColor: '#0F5B30',
                    timer: 1500
                });
                loadSettings();
            } else {
                Swal.fire('Gagal', data.message || 'Gagal menyimpan profil', 'error');
            }
        } catch (err) {
            Swal.fire('Error', 'Terjadi kesalahan koneksi', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // 2. Ganti Kata Sandi
    const handleChangePassword = async (e: React.FormEvent) => {
        e.preventDefault();

        if (newPassword !== confirmPassword) {
            return Swal.fire('Kata Sandi Tidak Cocok', 'Konfirmasi kata sandi tidak sama dengan kata sandi baru.', 'warning');
        }

        if (newPassword.length < 6) {
            return Swal.fire('Kata Sandi Lemah', 'Kata sandi baru harus memiliki minimal 6 karakter.', 'warning');
        }

        setIsSaving(true);
        try {
            const token = localStorage.getItem('khazanah_token');
            const res = await fetch(`${API_ENDPOINTS.auth}/change-password`, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    id: currentUser.id,
                    email: currentUser.email,
                    currentPassword,
                    newPassword
                })
            });

            const data = await res.json();
            if (res.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Kata Sandi Berhasil Diubah!',
                    text: 'Gunakan kata sandi baru Anda untuk sesi berikutnya.',
                    confirmButtonColor: '#0F5B30'
                });
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
            } else {
                Swal.fire('Gagal', data.message || 'Gagal mengubah kata sandi', 'error');
            }
        } catch (err) {
            Swal.fire('Error', 'Terjadi kesalahan koneksi', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // 3. Simpan Pengaturan PIN & Pendaftaran
    const handleSaveSecurityPin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const token = localStorage.getItem('khazanah_token');
            const res = await fetch(API_ENDPOINTS.settings, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    admin_register_pin: settingsData.admin_register_pin,
                    allow_admin_registration: settingsData.allow_admin_registration
                })
            });

            if (res.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Pengaturan PIN Tersimpan!',
                    text: 'PIN pendaftaran admin baru berhasil diperbarui.',
                    confirmButtonColor: '#0F5B30',
                    timer: 1500
                });
            } else {
                Swal.fire('Gagal', 'Gagal memperbarui PIN keamanan', 'error');
            }
        } catch (err) {
            Swal.fire('Error', 'Gagal terhubung ke server', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // 4. Simpan Identitas Platform & Kontak
    const handleSavePlatform = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const token = localStorage.getItem('khazanah_token');
            const res = await fetch(API_ENDPOINTS.settings, {
                method: 'PUT',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    platform_name: settingsData.platform_name,
                    platform_tagline: settingsData.platform_tagline,
                    official_email: settingsData.official_email,
                    official_whatsapp: settingsData.official_whatsapp,
                    instagram_account: settingsData.instagram_account,
                    address: settingsData.address
                })
            });

            if (res.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Identitas Platform Tersimpan!',
                    text: 'Data kontak dan identitas platform berhasil disimpan.',
                    confirmButtonColor: '#0F5B30',
                    timer: 1500
                });
            } else {
                Swal.fire('Gagal', 'Gagal menyimpan pengaturan platform', 'error');
            }
        } catch (err) {
            Swal.fire('Error', 'Gagal terhubung ke server', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // 5. Tambah Admin Baru
    const handleCreateAdmin = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newAdminForm.name || !newAdminForm.email || !newAdminForm.password) {
            return Swal.fire('Data Tidak Lengkap', 'Nama, email, dan kata sandi wajib diisi.', 'warning');
        }

        setIsSaving(true);
        try {
            const token = localStorage.getItem('khazanah_token');
            const res = await fetch(`${API_ENDPOINTS.auth}/users`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newAdminForm)
            });

            const data = await res.json();
            if (res.ok) {
                Swal.fire({
                    icon: 'success',
                    title: 'Admin Berhasil Ditambahkan!',
                    text: `Akun untuk ${newAdminForm.name} siap digunakan.`,
                    confirmButtonColor: '#0F5B30'
                });
                setIsAddUserModalOpen(false);
                setNewAdminForm({ name: '', email: '', password: '', role: 'admin' });
                loadSettings();
            } else {
                Swal.fire('Gagal', data.message || 'Gagal membuat admin', 'error');
            }
        } catch (err) {
            Swal.fire('Error', 'Gagal menghubungi server', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    // 6. Hapus Admin
    const handleDeleteAdmin = async (user: AdminUserItem) => {
        if (user.id === currentUser.id) {
            return Swal.fire('Tidak Diizinkan', 'Anda tidak dapat menghapus akun Anda sendiri yang sedang digunakan.', 'warning');
        }

        const confirm = await Swal.fire({
            title: `Hapus Akun ${user.name}?`,
            text: `Akun admin (${user.email}) akan dihapus secara permanen.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#0F5B30',
            confirmButtonText: 'Ya, Hapus!',
            cancelButtonText: 'Batal'
        });

        if (confirm.isConfirmed) {
            try {
                const token = localStorage.getItem('khazanah_token');
                const res = await fetch(`${API_ENDPOINTS.auth}/users/${user.id}?current_id=${currentUser.id}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                const data = await res.json();
                if (res.ok) {
                    Swal.fire('Terhapus!', 'Akun admin berhasil dihapus.', 'success');
                    loadSettings();
                } else {
                    Swal.fire('Gagal', data.message || 'Gagal menghapus user', 'error');
                }
            } catch (err) {
                Swal.fire('Error', 'Gagal menghapus user', 'error');
            }
        }
    };

    // 7. Unduh Cadangan JSON
    const handleDownloadBackup = async () => {
        try {
            const res = await fetch(`${API_ENDPOINTS.reports}/backup`);
            if (res.ok) {
                const blob = await res.blob();
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `khazanah_backup_${new Date().toISOString().slice(0, 10)}.json`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                Swal.fire({
                    icon: 'success',
                    title: 'Cadangan Diunduh!',
                    text: 'Snapshot database lengkap telah berhasil diunduh.',
                    confirmButtonColor: '#0F5B30'
                });
            } else {
                Swal.fire('Gagal', 'Gagal mengunduh cadangan data', 'error');
            }
        } catch (e) {
            Swal.fire('Error', 'Terjadi kesalahan koneksi', 'error');
        }
    };

    const copyRegisterLink = () => {
        const link = `${window.location.origin}/admin/register`;
        navigator.clipboard.writeText(link);
        Swal.fire({
            icon: 'success',
            title: 'Tautan Disalin!',
            text: 'Tautan registrasi admin telah disalin ke clipboard.',
            timer: 1500,
            showConfirmButton: false
        });
    };

    if (isLoading) {
        return (
            <div className="flex h-[70vh] flex-col items-center justify-center text-gray-400">
                <Loader2 className="animate-spin text-emerald mb-3" size={42} />
                <span className="font-outfit font-bold text-base text-gray-700">Memuat Pengaturan Sistem...</span>
            </div>
        );
    }

    return (
        <div className="space-y-8 font-sans">

            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-200/60 pb-6">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-emerald/10 text-emerald flex items-center justify-center border border-emerald/20 shadow-sm">
                        <Settings size={26} />
                    </div>
                    <div>
                        <h1 className="text-2xl lg:text-3xl font-black font-outfit text-gray-900 tracking-tight">
                            Pengaturan Sistem & Profil
                        </h1>
                        <p className="text-xs lg:text-sm text-gray-500 font-medium">
                            Kelola konfigurasi platform Khazanah, profil admin, kata sandi, dan keamanan
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 text-xs font-bold text-gray-500 bg-white px-3.5 py-2 rounded-xl border border-gray-100 shadow-sm">
                    <Shield size={14} className="text-emerald" />
                    <span>Role: <strong className="text-gray-800 uppercase">{currentUser.role || 'Admin'}</strong></span>
                </div>
            </div>

            {/* MAIN CONTENT WITH SIDE TABS */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

                {/* TAB LIST (LEFT) */}
                <div className="lg:col-span-3 bg-white rounded-3xl p-3 shadow-sm border border-gray-100 space-y-1">
                    <button
                        onClick={() => setActiveTab('profile')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-xs lg:text-sm transition-all text-left ${activeTab === 'profile' ? 'bg-emerald text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <User size={18} />
                        <span>Profil Admin</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('security')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-xs lg:text-sm transition-all text-left ${activeTab === 'security' ? 'bg-emerald text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Lock size={18} />
                        <span>Kata Sandi & Keamanan</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('pin')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-xs lg:text-sm transition-all text-left ${activeTab === 'pin' ? 'bg-emerald text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <KeyRound size={18} />
                        <span>PIN Registrasi Admin</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('platform')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-xs lg:text-sm transition-all text-left ${activeTab === 'platform' ? 'bg-emerald text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Globe size={18} />
                        <span>Identitas & Kontak</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('admins')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-xs lg:text-sm transition-all text-left ${activeTab === 'admins' ? 'bg-emerald text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Users size={18} />
                        <span>Kelola Staf Admin ({adminList.length})</span>
                    </button>

                    <button
                        onClick={() => setActiveTab('backup')}
                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl font-bold text-xs lg:text-sm transition-all text-left ${activeTab === 'backup' ? 'bg-emerald text-white shadow-sm' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                        <Database size={18} />
                        <span>Cadangan & Diagnostik</span>
                    </button>
                </div>

                {/* TAB PANELS (RIGHT) */}
                <div className="lg:col-span-9 space-y-6">

                    {/* TAB 1: PROFIL ADMIN */}
                    {activeTab === 'profile' && (
                        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                            <div className="flex items-center gap-4 mb-8 pb-6 border-b border-gray-100">
                                <div className="w-16 h-16 rounded-2xl bg-[#0F5B30] text-white flex items-center justify-center font-black text-2xl border-2 border-gold/30 shadow-inner uppercase">
                                    {profileName.charAt(0) || 'A'}
                                </div>
                                <div>
                                    <h3 className="text-xl font-black font-outfit text-gray-900">{profileName || 'Admin'}</h3>
                                    <p className="text-xs text-gray-500 font-medium">{profileEmail}</p>
                                    <span className="inline-block px-2.5 py-0.5 rounded-full bg-emerald-50 text-emerald text-[10px] font-bold mt-1">
                                        Super Administrator Aktif
                                    </span>
                                </div>
                            </div>

                            <form onSubmit={handleSaveProfile} className="space-y-5 max-w-xl">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        Nama Lengkap
                                    </label>
                                    <input
                                        type="text"
                                        value={profileName}
                                        onChange={(e) => setProfileName(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        Alamat Email Admin
                                    </label>
                                    <input
                                        type="email"
                                        value={profileEmail}
                                        onChange={(e) => setProfileEmail(e.target.value)}
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1">
                                        Email ini digunakan saat masuk ke dashboard admin.
                                    </p>
                                </div>

                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-3 bg-emerald hover:bg-emerald/90 text-white font-bold rounded-xl text-sm shadow-sm transition-all disabled:opacity-50"
                                    >
                                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                        <span>Simpan Perubahan Profil</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* TAB 2: KEAMANAN & KATA SANDI */}
                    {activeTab === 'security' && (
                        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                            <div className="mb-6">
                                <h3 className="text-xl font-black font-outfit text-gray-900">Ubah Kata Sandi</h3>
                                <p className="text-xs text-gray-500 font-medium mt-1">
                                    Pastikan kata sandi baru Anda kuat, terdiri dari minimal 6 karakter kombinasi huruf dan angka.
                                </p>
                            </div>

                            <form onSubmit={handleChangePassword} className="space-y-5 max-w-xl">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        Kata Sandi Saat Ini
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showCurrentPass ? 'text' : 'password'}
                                            value={currentPassword}
                                            onChange={(e) => setCurrentPassword(e.target.value)}
                                            placeholder="Masukkan kata sandi lama"
                                            required
                                            className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowCurrentPass(!showCurrentPass)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                                        >
                                            {showCurrentPass ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        Kata Sandi Baru
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showNewPass ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Minimal 6 karakter"
                                            required
                                            className="w-full px-4 py-3 pr-12 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowNewPass(!showNewPass)}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                                        >
                                            {showNewPass ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        Konfirmasi Kata Sandi Baru
                                    </label>
                                    <input
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Ulangi kata sandi baru"
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                </div>

                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-3 bg-emerald hover:bg-emerald/90 text-white font-bold rounded-xl text-sm shadow-sm transition-all disabled:opacity-50"
                                    >
                                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                                        <span>Perbarui Kata Sandi</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* TAB 3: PIN KEAMANAN & REGISTRASI ADMIN */}
                    {activeTab === 'pin' && (
                        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100 space-y-8">
                            <div>
                                <h3 className="text-xl font-black font-outfit text-gray-900">PIN Registrasi Admin</h3>
                                <p className="text-xs text-gray-500 font-medium mt-1">
                                    PIN ini wajib dimasukkan oleh calon admin/staf baru sebelum dapat membuka formulir registrasi admin di halaman pendaftaran.
                                </p>
                            </div>

                            <form onSubmit={handleSaveSecurityPin} className="space-y-6 max-w-xl">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        PIN Keamanan Aktif
                                    </label>
                                    <input
                                        type="text"
                                        value={settingsData.admin_register_pin}
                                        onChange={(e) => setSettingsData({ ...settingsData, admin_register_pin: e.target.value })}
                                        maxLength={10}
                                        required
                                        className="w-full px-4 py-3.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-lg font-mono font-bold tracking-widest text-emerald transition-all"
                                    />
                                    <p className="text-[11px] text-gray-400 mt-1.5">
                                        Hanya bagikan PIN ini kepada staf atau relawan internal terpercaya.
                                    </p>
                                </div>

                                <div className="p-4 rounded-2xl bg-amber-50/70 border border-amber-200/60 flex items-start gap-3">
                                    <AlertTriangle size={18} className="text-amber-700 shrink-0 mt-0.5" />
                                    <div className="text-xs text-amber-900 leading-relaxed">
                                        <strong>Penting:</strong> Jika ada staf internal yang keluar atau terjadi pergantian tim, segera ganti PIN pendaftaran ini demi menjaga keamanan sistem Khazanah.
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        Status Pendaftaran Admin Baru
                                    </label>
                                    <div className="flex items-center gap-4">
                                        <button
                                            type="button"
                                            onClick={() => setSettingsData({ ...settingsData, allow_admin_registration: 'true' })}
                                            className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 border transition-all ${settingsData.allow_admin_registration === 'true' ? 'bg-emerald text-white border-emerald shadow-sm' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                                        >
                                            <CheckCircle2 size={16} /> Buka Pendaftaran
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSettingsData({ ...settingsData, allow_admin_registration: 'false' })}
                                            className={`px-4 py-2.5 rounded-xl text-xs font-bold flex items-center gap-2 border transition-all ${settingsData.allow_admin_registration === 'false' ? 'bg-red-600 text-white border-red-600 shadow-sm' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                                        >
                                            <X size={16} /> Tutup Pendaftaran
                                        </button>
                                    </div>
                                </div>

                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-3 bg-emerald hover:bg-emerald/90 text-white font-bold rounded-xl text-sm shadow-sm transition-all disabled:opacity-50"
                                    >
                                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                        <span>Simpan Konfigurasi PIN</span>
                                    </button>
                                </div>
                            </form>

                            {/* Link Registrasi Cepat */}
                            <div className="pt-6 border-t border-gray-100">
                                <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block mb-2">
                                    Tautan Registrasi Admin Publik
                                </span>
                                <div className="flex items-center gap-2 max-w-xl">
                                    <input
                                        type="text"
                                        readOnly
                                        value={`${window.location.origin}/admin/register`}
                                        className="flex-1 px-4 py-2.5 rounded-xl bg-gray-100 text-gray-600 text-xs font-mono select-all border border-gray-200 outline-none"
                                    />
                                    <button
                                        type="button"
                                        onClick={copyRegisterLink}
                                        className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-gray-800 hover:bg-gray-900 text-white font-bold text-xs transition-colors"
                                    >
                                        <Copy size={14} /> Salin Tautan
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* TAB 4: IDENTITAS PLATFORM & KONTAK */}
                    {activeTab === 'platform' && (
                        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                            <div className="mb-6">
                                <h3 className="text-xl font-black font-outfit text-gray-900">Identitas & Kontak Platform</h3>
                                <p className="text-xs text-gray-500 font-medium mt-1">
                                    Informasi ini ditampilkan di halaman publik, formulir pendaftaran, dan footer website Khazanah.
                                </p>
                            </div>

                            <form onSubmit={handleSavePlatform} className="space-y-5 max-w-xl">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        Nama Platform
                                    </label>
                                    <input
                                        type="text"
                                        value={settingsData.platform_name}
                                        onChange={(e) => setSettingsData({ ...settingsData, platform_name: e.target.value })}
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2">
                                        Slogan / Tagline
                                    </label>
                                    <input
                                        type="text"
                                        value={settingsData.platform_tagline}
                                        onChange={(e) => setSettingsData({ ...settingsData, platform_tagline: e.target.value })}
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 flex items-center gap-1.5">
                                        <Mail size={14} /> Email Narahubung Resmi
                                    </label>
                                    <input
                                        type="email"
                                        value={settingsData.official_email}
                                        onChange={(e) => setSettingsData({ ...settingsData, official_email: e.target.value })}
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 flex items-center gap-1.5">
                                        <Smartphone size={14} /> WhatsApp Official
                                    </label>
                                    <input
                                        type="text"
                                        value={settingsData.official_whatsapp}
                                        onChange={(e) => setSettingsData({ ...settingsData, official_whatsapp: e.target.value })}
                                        placeholder="08xxxxxxxxxx"
                                        required
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 flex items-center gap-1.5">
                                        <Camera size={14} /> Akun Instagram
                                    </label>
                                    <input
                                        type="text"
                                        value={settingsData.instagram_account}
                                        onChange={(e) => setSettingsData({ ...settingsData, instagram_account: e.target.value })}
                                        placeholder="@khazanah_id"
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-2 flex items-center gap-1.5">
                                        <MapPin size={14} /> Alamat / Sekretariat
                                    </label>
                                    <input
                                        type="text"
                                        value={settingsData.address}
                                        onChange={(e) => setSettingsData({ ...settingsData, address: e.target.value })}
                                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium transition-all"
                                    />
                                </div>

                                <div className="pt-4">
                                    <button
                                        type="submit"
                                        disabled={isSaving}
                                        className="flex items-center gap-2 px-6 py-3 bg-emerald hover:bg-emerald/90 text-white font-bold rounded-xl text-sm shadow-sm transition-all disabled:opacity-50"
                                    >
                                        {isSaving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
                                        <span>Simpan Data Platform</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {/* TAB 5: KELOLA STAF ADMIN */}
                    {activeTab === 'admins' && (
                        <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100 space-y-6">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                <div>
                                    <h3 className="text-xl font-black font-outfit text-gray-900">
                                        Daftar Administrator Terdaftar
                                    </h3>
                                    <p className="text-xs text-gray-500 font-medium">
                                        Kelola akun yang memiliki hak akses penuh ke Admin Panel Khazanah
                                    </p>
                                </div>
                                <button
                                    onClick={() => setIsAddUserModalOpen(true)}
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-emerald hover:bg-emerald/90 text-white font-bold text-xs shadow-sm transition-all"
                                >
                                    <Plus size={16} /> Tambah Admin Baru
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-gray-400 uppercase font-black tracking-wider">
                                            <th className="py-3 px-4">Nama Lengkap</th>
                                            <th className="py-3 px-4">Email</th>
                                            <th className="py-3 px-4">Peran</th>
                                            <th className="py-3 px-4">Terdaftar Sejak</th>
                                            <th className="py-3 px-4 text-right">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50 font-medium text-gray-700">
                                        {adminList.map((adm) => {
                                            const isSelf = adm.id === currentUser.id;
                                            return (
                                                <tr key={adm.id} className="hover:bg-gray-50/80 transition-colors">
                                                    <td className="py-4 px-4 font-bold text-gray-900 flex items-center gap-2.5">
                                                        <div className="w-8 h-8 rounded-lg bg-emerald/10 text-emerald flex items-center justify-center font-bold">
                                                            {adm.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <div>{adm.name}</div>
                                                            {isSelf && (
                                                                <span className="text-[10px] text-emerald font-bold">(Akun Anda)</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="py-4 px-4 text-gray-500">{adm.email}</td>
                                                    <td className="py-4 px-4">
                                                        <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald font-bold text-[10px] uppercase">
                                                            {adm.role || 'Admin'}
                                                        </span>
                                                    </td>
                                                    <td className="py-4 px-4 text-gray-400">
                                                        {adm.created_at ? new Date(adm.created_at).toLocaleDateString('id-ID', { dateStyle: 'medium' }) : '-'}
                                                    </td>
                                                    <td className="py-4 px-4 text-right">
                                                        {!isSelf ? (
                                                            <button
                                                                onClick={() => handleDeleteAdmin(adm)}
                                                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                                                                title="Hapus Akun Admin"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-300 text-[11px]">-</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* TAB 6: CADANGAN & DIAGNOSTIK */}
                    {activeTab === 'backup' && (
                        <div className="space-y-6">
                            {/* Backup Card */}
                            <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-2xl bg-emerald/10 text-emerald flex items-center justify-center">
                                        <Database size={20} />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black font-outfit text-gray-900">
                                            Cadangan Snapshot Data (JSON Backup)
                                        </h3>
                                        <p className="text-xs text-gray-500 font-medium">
                                            Unduh salinan arsip data program, member, leader, dan curhatan dalam satu berkas terstruktur
                                        </p>
                                    </div>
                                </div>

                                <p className="text-xs text-gray-600 leading-relaxed max-w-2xl mb-6">
                                    Berkas cadangan mencakup metadata waktu ekspor, seluruh tabel program dakwah, formulir Sholehah, formulir Jofisah, rekrutmen Leaders, dan database pembicara. Berkas ini dapat digunakan untuk audit ataupun restorasi data.
                                </p>

                                <button
                                    onClick={handleDownloadBackup}
                                    className="flex items-center gap-2 px-6 py-3 bg-emerald hover:bg-emerald/90 text-white font-bold rounded-xl text-sm shadow-sm transition-all"
                                >
                                    <Download size={16} /> Unduh Snapshot Database (.json)
                                </button>
                            </div>

                            {/* System Diagnostics Info */}
                            <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-sm border border-gray-100">
                                <h4 className="text-sm font-black font-outfit text-gray-900 uppercase tracking-wider mb-4">
                                    Informasi Lingkungan & Status Sistem
                                </h4>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs">
                                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                        <span className="text-gray-400 font-bold block mb-1">Status Database</span>
                                        <div className="flex items-center gap-1.5 font-black text-emerald text-sm">
                                            <span className="w-2 h-2 rounded-full bg-emerald inline-block animate-pulse"></span>
                                            MySQL Terhubung
                                        </div>
                                    </div>

                                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                        <span className="text-gray-400 font-bold block mb-1">Versi Antarmuka</span>
                                        <div className="font-black text-gray-800 text-sm">v1.2.0 (Khazanah Web)</div>
                                    </div>

                                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100">
                                        <span className="text-gray-400 font-bold block mb-1">Autentikasi</span>
                                        <div className="font-black text-gray-800 text-sm">JWT Token (7 Hari)</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>

            {/* MODAL TAMBAH ADMIN BARU */}
            {isAddUserModalOpen && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-xs flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-3xl p-6 lg:p-8 shadow-2xl max-w-md w-full border border-gray-100 animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                            <div className="flex items-center gap-2.5 text-gray-900">
                                <Users size={20} className="text-emerald" />
                                <h3 className="text-lg font-black font-outfit">Tambah Admin Baru</h3>
                            </div>
                            <button
                                onClick={() => setIsAddUserModalOpen(false)}
                                className="text-gray-400 hover:text-gray-600 p-1"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleCreateAdmin} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                                    Nama Lengkap
                                </label>
                                <input
                                    type="text"
                                    value={newAdminForm.name}
                                    onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                                    required
                                    placeholder="cth. Fatimah Az-Zahra"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                                    Alamat Email
                                </label>
                                <input
                                    type="email"
                                    value={newAdminForm.email}
                                    onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
                                    required
                                    placeholder="admin@khazanah.id"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                                    Kata Sandi Sementara
                                </label>
                                <input
                                    type="password"
                                    value={newAdminForm.password}
                                    onChange={(e) => setNewAdminForm({ ...newAdminForm, password: e.target.value })}
                                    required
                                    placeholder="Minimal 6 karakter"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-600 mb-1.5">
                                    Peran / Role
                                </label>
                                <select
                                    value={newAdminForm.role}
                                    onChange={(e) => setNewAdminForm({ ...newAdminForm, role: e.target.value })}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-emerald text-sm font-medium"
                                >
                                    <option value="admin">Administrator</option>
                                    <option value="superadmin">Super Administrator</option>
                                    <option value="staff">Staf Internal</option>
                                </select>
                            </div>

                            <div className="pt-4 flex items-center justify-end gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsAddUserModalOpen(false)}
                                    className="px-4 py-2.5 rounded-xl text-gray-600 hover:bg-gray-100 font-bold text-xs transition-colors"
                                >
                                    Batal
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className="px-5 py-2.5 rounded-xl bg-emerald hover:bg-emerald/90 text-white font-bold text-xs shadow-sm transition-all disabled:opacity-50"
                                >
                                    {isSaving ? 'Menyimpan...' : 'Buat Akun Admin'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
};

export default SettingsAdmin;
